/**
 * 游戏初始化服务
 * 整合势力创建和人物初始化
 */

import type { GameDate } from '../types/game'
import type { FactionInstance } from '../types/faction'
import type { Family } from '../types/family'
import type { CharacterArchive } from '../types/characterArchive'
import { useFactionStore } from '../stores/factionStore'
import { useFamilyStore } from '../stores/familyStore'
import { useCharacterStore } from '../stores/characterStore'
import { getFactionTemplate } from '../data/factionTemplates'
import { getFactionRoyalArchive, getRulerName } from '../data/royalFamilyArchives'
import { createFactionFamilies, createCharacterArchive } from './archiveService'

// ============================================
// 势力配置 (临时，后续应从剧本配置加载)
// ============================================

interface FactionInitConfig {
  templateId: string
  cityIds: string[]
  customName?: string
  customRulerName?: string
  isPlayer?: boolean
}

// 商朝剧本默认势力配置
const SHANG_SCENARIO_FACTIONS: FactionInitConfig[] = [
  {
    templateId: 'shang',
    cityIds: ['anyang', 'zhaoge', 'chaoge'],
    isPlayer: true,
  },
  {
    templateId: 'zhou',
    cityIds: ['feng', 'qi'],
    customRulerName: '姬昌',
  },
  {
    templateId: 'guifang',
    cityIds: ['guifang_camp1', 'guifang_camp2'],
  },
  {
    templateId: 'qiangfang',
    cityIds: ['qiangfang_camp1'],
  },
]

// 春秋剧本默认势力配置
const SPRING_AUTUMN_SCENARIO_FACTIONS: FactionInitConfig[] = [
  {
    templateId: 'qi',
    cityIds: ['linzi'],
    isPlayer: true,
  },
  {
    templateId: 'jin',
    cityIds: ['jiang'],
  },
  {
    templateId: 'chu',
    cityIds: ['yingdu'],
  },
  {
    templateId: 'qin',
    cityIds: ['yong'],
  },
  {
    templateId: 'wu',
    cityIds: ['suzhou'],
  },
  {
    templateId: 'yue',
    cityIds: ['shaoxing'],
  },
]

// ============================================
// 游戏初始化结果
// ============================================

export interface GameInitResult {
  success: boolean
  playerFactionId: string | null
  factions: Map<string, FactionInstance>
  families: Map<string, Family>
  characterArchives: Map<string, CharacterArchive>
  characterStats: {
    totalCharacters: number
    factionStats: Map<string, {
      total: number
      royal: number
      official: number
      commoner: number
    }>
  }
  errors: string[]
}

// ============================================
// 主要初始化函数
// ============================================

/**
 * 初始化新游戏
 * @param scenarioId 剧本ID
 * @param playerFactionTemplateId 玩家选择的势力模板ID
 * @param startDate 游戏开始日期
 */
export function initializeNewGame(
  scenarioId: string,
  playerFactionTemplateId: string,
  startDate: GameDate
): GameInitResult {
  const result: GameInitResult = {
    success: true,
    playerFactionId: null,
    factions: new Map(),
    families: new Map(),
    characterArchives: new Map(),
    characterStats: {
      totalCharacters: 0,
      factionStats: new Map(),
    },
    errors: [],
  }

  try {
    // 获取玩家势力的固定君主名
    const playerRulerName = getRulerName(scenarioId, playerFactionTemplateId) || '玩家'

    // 1. 获取势力配置
    const factionConfigs = getScenarioFactionConfigs(scenarioId, playerFactionTemplateId, playerRulerName)

    // 2. 创建势力
    const factionResults = createFactions(factionConfigs, startDate)
    result.factions = factionResults.factions
    result.playerFactionId = factionResults.playerFactionId

    // 3. 为各势力初始化家族和人物档案
    const allFamilies: Family[] = []
    const allCharacterArchives: CharacterArchive[] = []

    factionConfigs.forEach(config => {
      const factionId = factionResults.factionIdMap.get(config.templateId)
      if (!factionId) {
        result.errors.push(`未找到势力实例ID: ${config.templateId}`)
        return
      }

      // 获取王族预设档案
      const royalArchive = getFactionRoyalArchive(scenarioId, config.templateId)

      // 创建家族
      const families = createFactionFamilies(factionId, scenarioId, startDate)

      // 如果有预设档案，使用预设创建人物
      if (royalArchive && royalArchive.members.length > 0) {
        // 更新统治家族信息
        const rulingFamily = families.find(f => f.isRulingFamily)
        if (rulingFamily) {
          rulingFamily.surname = royalArchive.rulingFamily.surname
          rulingFamily.clanName = royalArchive.rulingFamily.clanName
          rulingFamily.name = royalArchive.rulingFamily.clanName
            ? `${royalArchive.rulingFamily.surname}${royalArchive.rulingFamily.clanName}`
            : `${royalArchive.rulingFamily.surname}氏`
          rulingFamily.fullName = royalArchive.rulingFamily.clanName
            ? `${royalArchive.rulingFamily.surname}姓${royalArchive.rulingFamily.clanName}氏`
            : `${royalArchive.rulingFamily.surname}姓`
        }

        // 创建预设人物档案
        royalArchive.members.forEach(preset => {
          const family = preset.identity === 'ruler' || preset.identity === 'royal'
            ? rulingFamily
            : families.find(f => !f.isRulingFamily)

          const birthDate: GameDate = {
            year: startDate.year + preset.birthYear,
            month: 1,
            day: 1,
          }

          const archive = createCharacterArchive(
            preset.name,
            preset.surname,
            preset.clanName,
            preset.identity,
            factionId,
            family?.id || '',
            birthDate,
            preset.gender,
            preset.identity === 'ruler'
          )

          // 应用预设能力
          if (preset.abilities) {
            Object.assign(archive.abilities, preset.abilities)
          }

          // 应用预设性格
          if (preset.personality) {
            Object.assign(archive.personality, preset.personality)
          }

          // 应用预设流派
          if (preset.school) {
            archive.school = preset.school
          }

          // 添加头衔
          if (preset.title) {
            archive.titles.push(preset.title)
          }

          allCharacterArchives.push(archive)

          // 更新家族成员
          if (family) {
            family.memberIds.push(archive.id)
            family.members.push({
              characterId: archive.id,
              name: preset.name,
              relation: preset.identity === 'ruler' ? '家主' : '成员',
              role: preset.identity === 'ruler' ? 'head' : 'member',
              joinDate: startDate,
              isAlive: true,
            })

            if (preset.identity === 'ruler') {
              family.headId = archive.id
            }
          }
        })
      }

      allFamilies.push(...families)

      // 记录统计
      const royalCount = royalArchive?.members.filter(m => m.identity === 'ruler' || m.identity === 'royal').length || 0
      const officialCount = royalArchive?.members.filter(m => m.identity === 'official' || m.identity === 'general').length || 0

      result.characterStats.factionStats.set(factionId, {
        total: royalArchive?.members.length || 0,
        royal: royalCount,
        official: officialCount,
        commoner: 0,
      })

      // 更新势力君主名
      const faction = result.factions.get(factionId)
      const rulerArchive = allCharacterArchives.find(
        a => a.factionId === factionId && a.identity === 'ruler'
      )
      if (faction && rulerArchive) {
        faction.ruler.name = rulerArchive.name
      }
    })

    // 4. 将家族和人物档案添加到结果
    allFamilies.forEach(family => {
      result.families.set(family.id, family)
    })
    allCharacterArchives.forEach(archive => {
      result.characterArchives.set(archive.id, archive)
    })
    result.characterStats.totalCharacters = allCharacterArchives.length

    // 5. 保存到 store
    saveToStores(result.factions, result.families, result.characterArchives, result.playerFactionId, startDate)

  } catch (error) {
    result.success = false
    result.errors.push(error instanceof Error ? error.message : '未知错误')
  }

  return result
}

// ============================================
// 辅助函数
// ============================================

/**
 * 获取剧本势力配置
 */
function getScenarioFactionConfigs(
  scenarioId: string,
  playerFactionTemplateId: string,
  playerRulerName: string
): FactionInitConfig[] {
  let configs: FactionInitConfig[]

  switch (scenarioId) {
    case 'shang':
      configs = [...SHANG_SCENARIO_FACTIONS]
      break
    case 'spring_autumn':
      configs = [...SPRING_AUTUMN_SCENARIO_FACTIONS]
      break
    default:
      configs = [...SPRING_AUTUMN_SCENARIO_FACTIONS]
  }

  // 标记玩家势力并设置君主名
  return configs.map(config => ({
    ...config,
    isPlayer: config.templateId === playerFactionTemplateId,
    customRulerName: config.templateId === playerFactionTemplateId
      ? playerRulerName
      : config.customRulerName,
  }))
}

/**
 * 创建势力
 */
function createFactions(
  configs: FactionInitConfig[],
  startDate: GameDate
): {
  factions: Map<string, FactionInstance>
  factionIdMap: Map<string, string>
  playerFactionId: string | null
} {
  const factions = new Map<string, FactionInstance>()
  const factionIdMap = new Map<string, string>()
  let playerFactionId: string | null = null
  let idCounter = 0

  configs.forEach(config => {
    const template = getFactionTemplate(config.templateId)
    if (!template) {
      console.warn(`Template not found: ${config.templateId}`)
      return
    }

    const factionId = `faction_${Date.now()}_${idCounter++}`
    factionIdMap.set(config.templateId, factionId)

    // 计算初始军力
    const military = Math.floor(template.baseResources.population / 10)

    const faction: FactionInstance = {
      instanceId: factionId,
      templateId: config.templateId,
      name: config.customName || template.baseProfile.name,
      type: template.baseProfile.type,
      color: template.baseProfile.color,
      emblem: template.baseProfile.emblem,
      status: 'active',
      foundedAt: startDate,
      ruler: {
        name: config.customRulerName || `${template.baseProfile.name}君`,
        title: template.baseProfile.type === 'royal' ? '王' :
               template.baseProfile.type === 'vassal' ? '侯' : '首领',
        traits: [],
        reignStart: startDate.year,
      },
      resources: {
        ...template.baseResources,
        military,
      },
      ownedCityIds: config.cityIds,
      capitalCityId: config.cityIds[0] || '',
      relations: {},
      vassalIds: [],
      technologies: [],
      policies: [],
      traits: template.traitTemplates.map(t => t.traitId),
      history: [{
        id: `event_${Date.now()}_${config.templateId}`,
        type: 'founded',
        year: startDate.year,
        month: startDate.month,
        title: `${config.customName || template.baseProfile.name}建立`,
        description: `${config.customName || template.baseProfile.name}于${startDate.year}年建立`,
      }],
    }

    factions.set(factionId, faction)

    if (config.isPlayer) {
      playerFactionId = factionId
    }
  })

  return { factions, factionIdMap, playerFactionId }
}

/**
 * 保存到 stores
 */
function saveToStores(
  factions: Map<string, FactionInstance>,
  families: Map<string, Family>,
  characterArchives: Map<string, CharacterArchive>,
  playerFactionId: string | null,
  startDate: GameDate
): void {
  // 保存到 faction store
  useFactionStore.setState({
    activeFactions: factions,
    playerFactionId,
    currentDate: startDate,
  })

  // 保存到 family store
  const familyArray = Array.from(families.values())
  useFamilyStore.getState().initializeFamilies(familyArray)

  // 保存到 character store (将 CharacterArchive 转换为 Character)
  const characters: import('../types/character').Character[] = Array.from(characterArchives.values()).map(archive => {
    // 计算年龄 (可用于后续逻辑)
      // const currentYear = startDate.year
      // const birthYear = archive.birthDate.year
      // const age = currentYear - birthYear

      // 转换 traits - CharacterArchive 中的 traits 是 CharacterTrait[] (来自 types/character)
    const validTraits: import('../types/character').CharacterTrait[] = ['brave', 'cautious', 'cunning', 'honest', 'ambitious', 'loyal', 
         'treacherous', 'generous', 'stingy', 'wise', 'foolish', 
         'charismatic', 'cruel', 'benevolent']
    const traits = archive.traits.filter((t): t is import('../types/character').CharacterTrait => 
      validTraits.includes(t as import('../types/character').CharacterTrait)
    )

    // 转换历史记录
    const history: import('../types/character').CharacterHistoryEvent[] = archive.experiences.map((exp, index) => ({
      id: `exp_${archive.id}_${index}`,
      date: exp.date,
      type: exp.type as 'birth' | 'appointment' | 'promotion' | 'demotion' | 'marriage' | 'birth_of_child' | 'battle' | 'death' | 'other',
      title: exp.title,
      description: exp.description,
      relatedCharacterIds: exp.relatedCharacterIds,
    }))

    return {
      id: archive.id,
      name: archive.name,
      factionId: archive.factionId,
      identity: archive.identity as import('../types/character').CharacterIdentity,
      gender: archive.gender,
      birthDate: archive.birthDate,
      deathDate: archive.deathDate,
      status: archive.isAlive ? 'active' : 'deceased',
      abilities: {
        intelligence: archive.abilities.intelligence,
        charisma: archive.abilities.charisma,
        military: archive.abilities.military,
        administration: archive.abilities.administration,
        diplomacy: archive.abilities.diplomacy,
        intrigue: archive.abilities.intrigue,
      },
      traits,
      loyalty: 50,
      reputation: (archive.reputation.honor + archive.reputation.respect) / 2,
      position: archive.currentPosition ? {
        title: archive.currentPosition.title,
        rank: archive.currentPosition.level,
        department: archive.currentPosition.department,
      } : undefined,
      relations: {
        fatherId: archive.fatherId,
        motherId: archive.motherId,
        spouseId: archive.spouseIds[0],
        childrenIds: archive.childrenIds,
        siblingIds: archive.siblingIds,
        friendIds: [],
        enemyIds: [],
      },
      history,
    }
  })
  useCharacterStore.getState().initializeCharacters(characters)

  console.log('Families initialized:', families.size)
  console.log('Character archives initialized:', characterArchives.size)
}

// ============================================
// 快捷初始化函数
// ============================================

/**
 * 快速开始游戏 (使用默认设置)
 */
export function quickStart(
  playerFactionTemplateId: string
): GameInitResult {
  const startDate: GameDate = { year: -770, month: 1, day: 1 } // 春秋开始
  return initializeNewGame(
    'spring_autumn',
    playerFactionTemplateId,
    startDate
  )
}

/**
 * 初始化商朝剧本
 */
export function initializeShangScenario(
  playerIsShang: boolean = true
): GameInitResult {
  const startDate: GameDate = { year: -1046, month: 1, day: 1 }
  const playerFaction = playerIsShang ? 'shang' : 'zhou'
  return initializeNewGame('shang', playerFaction, startDate)
}

// ============================================
// 统计信息
// ============================================

export function printInitResult(result: GameInitResult): void {
  console.log('========== 游戏初始化结果 ==========')
  console.log(`成功: ${result.success}`)
  console.log(`玩家势力: ${result.playerFactionId}`)
  console.log(`势力数量: ${result.factions.size}`)
  console.log(`人物总数: ${result.characterStats.totalCharacters}`)

  console.log('\n---------- 各势力人物统计 ----------')
  result.characterStats.factionStats.forEach((stats, factionId) => {
    const faction = result.factions.get(factionId)
    console.log(`${faction?.name || factionId}:`)
    console.log(`  总计: ${stats.total}`)
    console.log(`  王室: ${stats.royal}`)
    console.log(`  朝臣: ${stats.official}`)
    console.log(`  平民: ${stats.commoner}`)
  })

  if (result.errors.length > 0) {
    console.log('\n---------- 错误 ----------')
    result.errors.forEach(err => console.log(`  - ${err}`))
  }

  console.log('====================================')
}
